(() => {
  const modal = document.querySelector('[data-altoff-profile-modal]');
  if (!modal) return;

  const panel = modal.querySelector('.af-profile__panel');
  const gallery = modal.querySelector('[data-altoff-profile-gallery]');
  const track = modal.querySelector('[data-altoff-profile-track]');
  const counter = modal.querySelector('[data-altoff-profile-counter]');
  const previousButton = modal.querySelector('[data-altoff-profile-prev]');
  const nextButton = modal.querySelector('[data-altoff-profile-next]');
  const closeButtons = [...modal.querySelectorAll('[data-altoff-profile-close], [data-altoff-profile-empty-close]')];
  const emptyClose = modal.querySelector('[data-altoff-profile-empty-close]');
  const instagram = modal.querySelector('[data-altoff-profile-instagram]');
  const note = modal.querySelector('[data-altoff-profile-note]');
  const productLink = modal.querySelector('[data-altoff-profile-product-link]');

  const library = new Map();
  let activePhotos = [];
  let activeIndex = 0;
  let scrollFrame = 0;
  let lastTrigger = null;
  let lockedScrollY = 0;
  let bodySnapshot = null;

  document.querySelectorAll('[data-altoff-community-entry]').forEach((node) => {
    try {
      const entry = JSON.parse(node.textContent);
      if (entry?.id) library.set(String(entry.id), entry);
    } catch (_) {
      // Ignore malformed entries so one record cannot break the library.
    }
  });

  const cleanValue = (value) => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'object') {
      if ('value' in value) return cleanValue(value.value);
      if ('amount' in value) return cleanValue(value.amount);
    }
    return String(value).trim();
  };

  const photoUrl = (photo) => cleanValue(photo?.imageLarge || photo?.image || photo?.url || photo);

  const normalizedPhotoKey = (value) => {
    const raw = cleanValue(value);
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.origin);
      ['width', 'height', 'crop', 'format'].forEach((key) => url.searchParams.delete(key));
      return `${url.origin}${url.pathname}`;
    } catch (_) {
      return raw.replace(/([?&])(width|height|crop|format)=[^&]*/g, '$1');
    }
  };

  const mergePhotos = (...groups) => {
    const seen = new Set();
    const merged = [];
    groups.flat().forEach((photo) => {
      const url = photoUrl(photo);
      const key = normalizedPhotoKey(url);
      if (!url || !key || seen.has(key)) return;
      seen.add(key);
      merged.push(typeof photo === 'string' ? { image: photo, imageLarge: photo } : photo);
    });
    return merged;
  };

  const fallbackProfile = (card) => {
    let profile = {};
    if (card.dataset.profile) {
      try { profile = JSON.parse(card.dataset.profile); } catch (_) { profile = {}; }
    }

    const fallbackImage = cleanValue(
      card.dataset.profileImage ||
      profile.imageLarge ||
      profile.image ||
      profile.coverImage?.imageLarge ||
      profile.coverImage?.image
    );

    return {
      id: cleanValue(card.dataset.profileId || profile.id),
      name: cleanValue(card.dataset.profileName || profile.name || profile.dogName),
      breed: cleanValue(card.dataset.profileBreed || profile.breed),
      sex: cleanValue(card.dataset.profileSex || profile.sex || profile.gender),
      age: cleanValue(card.dataset.profileAge || profile.age),
      product: cleanValue(card.dataset.profileProduct || profile.product || profile.productTitle),
      productUrl: cleanValue(card.dataset.profileProductUrl || profile.productUrl),
      size: cleanValue(card.dataset.profileSize || profile.size),
      colour: cleanValue(card.dataset.profileColour || profile.colour || profile.color),
      weight: cleanValue(card.dataset.profileWeight || profile.weight),
      back: cleanValue(card.dataset.profileBack || profile.back),
      chest: cleanValue(card.dataset.profileChest || profile.chest),
      neck: cleanValue(card.dataset.profileNeck || profile.neck),
      note: cleanValue(card.dataset.profileNote || profile.note || profile.caption),
      instagramHandle: cleanValue(card.dataset.profileInstagram || profile.instagramHandle),
      instagramUrl: cleanValue(card.dataset.profileInstagramUrl || profile.instagramUrl),
      photos: Array.isArray(profile.photos)
        ? profile.photos
        : (fallbackImage ? [{ image: fallbackImage, imageLarge: fallbackImage }] : []),
    };
  };

  const profileFromCard = (card) => {
    const fallback = fallbackProfile(card);
    const shared = fallback.id ? library.get(fallback.id) : null;
    if (!shared) return fallback;
    return {
      ...fallback,
      ...shared,
      colour: cleanValue(shared.colour) || fallback.colour,
      productUrl: cleanValue(shared.productUrl) || fallback.productUrl,
      photos: mergePhotos(fallback.photos || [], shared.photos || []),
    };
  };

  const setText = (selector, value, suffix = '') => {
    const node = modal.querySelector(selector);
    if (!node) return;
    const cleaned = cleanValue(value);
    node.textContent = cleaned ? `${cleaned}${suffix}` : '—';
  };

  const buildMeta = (profile) => {
    const identity = [profile.sex, profile.breed].filter(Boolean).join(' ');
    const age = cleanValue(profile.age);
    const ageLabel = age && !/year|month/i.test(age) ? `${age} years` : age;
    return [identity || profile.breed, ageLabel, profile.weight ? `${profile.weight} kg` : '']
      .filter(Boolean)
      .join(' · ');
  };

  const setGalleryHeight = () => {
    const viewportHeight = window.visualViewport?.height || window.innerHeight || 800;
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const target = Math.round(viewportHeight * (mobile ? 0.62 : 0.78));
    const minimum = mobile ? 390 : 460;
    const maximum = mobile ? 720 : 780;
    modal.style.setProperty('--af-gallery-height', `${Math.max(minimum, Math.min(maximum, target))}px`);
  };

  const updateGalleryState = (index) => {
    activeIndex = Math.max(0, Math.min(index, Math.max(0, activePhotos.length - 1)));
    const hasMultiple = activePhotos.length > 1;
    counter.hidden = !hasMultiple;
    counter.textContent = hasMultiple ? `${activeIndex + 1} / ${activePhotos.length}` : '';
    previousButton.hidden = !hasMultiple || activeIndex === 0;
    nextButton.hidden = !hasMultiple || activeIndex === activePhotos.length - 1;
  };

  const renderGallery = (profile) => {
    activePhotos = mergePhotos(profile.photos || []).map((photo) => ({
      url: photoUrl(photo),
      width: Number(photo?.width) || 0,
      height: Number(photo?.height) || 0,
    })).filter((photo) => photo.url);

    track.replaceChildren();

    activePhotos.forEach((photo, index) => {
      const slide = document.createElement('div');
      slide.className = 'af-profile__slide';

      const image = document.createElement('img');
      image.src = photo.url;
      image.alt = profile.name
        ? `${profile.name} community fit photo ${index + 1}`
        : `Community fit photo ${index + 1}`;
      image.loading = index === 0 ? 'eager' : 'lazy';
      image.decoding = 'async';
      image.draggable = false;

      slide.appendChild(image);
      track.appendChild(slide);
    });

    const hasPhotos = activePhotos.length > 0;
    gallery.hidden = !hasPhotos;
    emptyClose.hidden = hasPhotos;
    track.scrollLeft = 0;
    updateGalleryState(0);
  };

  const scrollToPhoto = (index) => {
    const width = track.clientWidth || 1;
    const safeIndex = Math.max(0, Math.min(index, activePhotos.length - 1));
    track.scrollTo({ left: width * safeIndex, behavior: 'smooth' });
    updateGalleryState(safeIndex);
  };

  track.addEventListener('scroll', () => {
    cancelAnimationFrame(scrollFrame);
    scrollFrame = requestAnimationFrame(() => {
      const width = track.clientWidth || 1;
      updateGalleryState(Math.round(track.scrollLeft / width));
    });
  }, { passive: true });

  previousButton?.addEventListener('click', () => scrollToPhoto(activeIndex - 1));
  nextButton?.addEventListener('click', () => scrollToPhoto(activeIndex + 1));

  const lockPage = () => {
    if (bodySnapshot) return;
    lockedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
    bodySnapshot = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };
    document.documentElement.classList.add('af-profile-is-open');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  };

  const unlockPage = () => {
    if (!bodySnapshot) return;
    Object.assign(document.body.style, bodySnapshot);
    document.documentElement.classList.remove('af-profile-is-open');
    bodySnapshot = null;
    window.scrollTo(0, lockedScrollY);
  };

  const openModal = (card) => {
    const profile = profileFromCard(card);
    lastTrigger = card;

    setText('[data-altoff-profile-name]', profile.name);
    setText('[data-altoff-profile-meta]', buildMeta(profile));
    setText('[data-altoff-profile-product]', profile.product);
    setText('[data-altoff-profile-size]', profile.size);

    const colourNode = modal.querySelector('[data-altoff-profile-colour]');
    if (colourNode) {
      colourNode.textContent = profile.colour ? `Colour: ${profile.colour}` : 'Colour not recorded';
      colourNode.hidden = false;
    }
    setText('[data-altoff-profile-back]', profile.back, profile.back ? ' cm' : '');
    setText('[data-altoff-profile-chest]', profile.chest, profile.chest ? ' cm' : '');
    setText('[data-altoff-profile-neck]', profile.neck, profile.neck ? ' cm' : '');

    if (profile.note) {
      note.textContent = profile.note;
      note.hidden = false;
    } else {
      note.textContent = '';
      note.hidden = true;
    }

    const handle = cleanValue(profile.instagramHandle).replace(/^@/, '');
    if (handle || profile.instagramUrl) {
      instagram.textContent = handle ? `@${handle}` : 'Instagram';
      instagram.href = profile.instagramUrl || `https://www.instagram.com/${encodeURIComponent(handle)}/`;
      instagram.hidden = false;
    } else {
      instagram.hidden = true;
      instagram.removeAttribute('href');
    }

    if (profile.productUrl) {
      productLink.href = profile.productUrl;
      productLink.innerHTML = 'Shop this look <span aria-hidden="true">→</span>';
      productLink.hidden = false;
    } else {
      productLink.href = '/collections/all';
      productLink.innerHTML = 'Shop dogwear <span aria-hidden="true">→</span>';
      productLink.hidden = false;
    }

    setGalleryHeight();
    renderGallery(profile);
    modal.hidden = false;
    panel.scrollTop = 0;
    lockPage();
    requestAnimationFrame(() => modal.classList.add('is-open'));
    window.setTimeout(() => closeButtons.find((button) => !button.hidden)?.focus(), 50);
  };

  const closeModal = () => {
    if (modal.hidden) return;
    modal.classList.remove('is-open');
    window.setTimeout(() => {
      modal.hidden = true;
      unlockPage();
      if (lastTrigger && document.contains(lastTrigger)) {
        lastTrigger.focus({ preventScroll: true });
      }
    }, 190);
  };

  document.addEventListener('click', (event) => {
    const card = event.target.closest('[data-altoff-profile-card]');
    if (!card) return;
    event.preventDefault();
    event.stopPropagation();
    openModal(card);
  });

  closeButtons.forEach((button) => button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeModal();
  }));

  modal.addEventListener('click', (event) => {
    if (event.target === modal) closeModal();
  });

  const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
  document.addEventListener('keydown', (event) => {
    if (modal.hidden) {
      if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('[data-altoff-profile-card]')) {
        event.preventDefault();
        openModal(event.target);
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal();
      return;
    }
    if (event.key === 'ArrowLeft' && activePhotos.length > 1) {
      event.preventDefault();
      scrollToPhoto(activeIndex - 1);
      return;
    }
    if (event.key === 'ArrowRight' && activePhotos.length > 1) {
      event.preventDefault();
      scrollToPhoto(activeIndex + 1);
      return;
    }
    if (event.key === 'Tab') {
      const items = [...panel.querySelectorAll(focusableSelector)].filter((item) => !item.hidden);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  const viewport = window.visualViewport;
  viewport?.addEventListener('resize', setGalleryHeight, { passive: true });
  window.addEventListener('orientationchange', setGalleryHeight, { passive: true });
})();
